import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router';
import { Command } from 'cmdk';
import { useMe } from '../lib/useMe';
import { loadRecents, pushRecent, type Recent } from '../lib/searchRecents';
import {
  Highlighted,
  TIER_LABEL,
  TIER_PIPS,
  TYPE_META,
  WithheldBanner,
  metaFor,
  whyFor,
} from '../lib/searchShared';
import { MIN_TERM, useGlobalSearch } from '../lib/useGlobalSearch';
import { Icon } from './Icon';

/**
 * The ⌘K command palette — find and jump to any entity in under three keystrokes
 * without leaving the page.
 *
 * Data, scope parsing and every derived count come from `useGlobalSearch`, shared
 * with `/search`. This file owns layout, keyboard behaviour and the idle state.
 *
 * ── mono note ────────────────────────────────────────────────────────────────
 * Identifiers here (hit titles, why-chip values, ⌘K hints) render in Plex MONO,
 * via the `search-*` classes only. That is a deliberate, scoped exception to the
 * os-internal-ui mono policy in globals.css, which reserves mono for code and says
 * "NOT resource names" — the design handoff for this feature asks for mono on
 * identifiers and was accepted on that point. It is contained to search: the
 * global rule is untouched, so detail pages still render names in sans, and
 * reverting means deleting the font-family lines from the search block.
 */

/** Rows fetched per entity type. The palette previews 4 (PER_GROUP) but keeps a
 *  deeper set so tabbing into a type shows more than the preview did. */
const LIMIT_PER_TYPE = 12;

/** Idle-state scope hints — each inserts a worked example, not a bare prefix,
 *  because a lone `secret:` searches nothing and looks broken. */
const SCOPE_HINTS: { prefix: string; what: string; example: string }[] = [
  { prefix: 'repo:', what: 'GitLab projects', example: 'repo:scio' },
  { prefix: 'cluster:', what: 'k8s clusters', example: 'cluster:prod' },
  { prefix: 'service:', what: 'deployed services', example: 'service:api' },
  { prefix: 'ns:', what: 'namespaces', example: 'ns:qa' },
  { prefix: 'secret:', what: 'names + keys', example: 'secret:MONGO' },
];

const relAge = (ms: number): string => {
  const s = Math.max(0, Math.floor((Date.now() - ms) / 1000));
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

export function GlobalSearchBar() {
  const navigate = useNavigate();
  const { me } = useMe();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const [tab, setTab] = useState('all');
  const [recents, setRecents] = useState<Recent[]>([]);
  const panelRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Focus is restored here on close — a palette that drops focus to <body> leaves
  // a keyboard user at the top of the document.
  const returnFocusRef = useRef<HTMLElement | null>(null);

  const s = useGlobalSearch({ value, tab, limitPerType: LIMIT_PER_TYPE, skip: !open });

  const close = useCallback(() => {
    setOpen(false);
    setValue('');
    setTab('all');
    returnFocusRef.current?.focus?.();
    returnFocusRef.current = null;
  }, []);

  const openPalette = useCallback(() => {
    returnFocusRef.current = document.activeElement as HTMLElement | null;
    setRecents(loadRecents());
    setOpen(true);
  }, []);

  // ⌘K / Ctrl+K toggles the palette anywhere in the app.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        if (open) close();
        else openPalette();
      } else if (e.key === 'Escape' && open) {
        close();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, close, openPalette]);

  // Click-outside closes. mousedown + containment (not a click on the overlay) so
  // a drag that starts inside the dialog and ends outside doesn't dismiss it.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) close();
    };
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, [open, close]);

  // Record the search once it has SETTLED, so recents hold queries that went
  // somewhere rather than every prefix typed on the way there.
  //
  // Gated on `settledTerm`, not on `!loading`: there is a window after the input
  // changes but before the debounce fires where nothing is loading and the counts
  // still belong to the previous term — that window logged `zzzqqq` (0 hits) as
  // "23 hits", inherited from the `weather` search before it.
  //
  // Zero-hit queries are skipped: "Recent" is a list you click to re-run, and a
  // row that reliably returns nothing has no reason to be on it.
  useEffect(() => {
    if (!open || !s.settledTerm || s.total === 0) return;
    const t = setTimeout(() => setRecents(pushRecent(value, s.total)), 900);
    return () => clearTimeout(t);
  }, [open, s.settledTerm, s.total, value]);

  const go = useCallback(
    (route: string) => {
      // Opening a result is proof the query was good, so record it even if the
      // settle timer hadn't fired yet — but only with a count we trust.
      if (s.settledTerm && s.total > 0) pushRecent(value, s.total);
      close();
      navigate(route);
    },
    [close, navigate, value, s.total, s.settledTerm],
  );

  const seeAll = useCallback(() => {
    const qs = new URLSearchParams({ q: value });
    if (tab !== 'all') qs.set('type', tab);
    close();
    navigate(`/search?${qs.toString()}`);
  }, [close, navigate, value, tab]);

  // Tab strip: "All types" then every type, each carrying its TRUE total.
  const tabs = [
    { key: 'all', label: 'All types', n: s.total, color: 'var(--muted)' },
    ...TYPE_META.map((m) => ({ key: m.type, label: m.label, n: s.counts[m.type] ?? 0, color: m.color })),
  ];

  // ⇥ advances to the next NON-EMPTY tab (Shift+⇥ goes back), which is why the
  // dialog can also trap Tab: inside the palette, Tab means "next type", and the
  // only other focusable thing is the input the user is already in.
  const cycleTab = useCallback(
    (dir: 1 | -1) => {
      const usable = tabs.filter((t) => t.key === 'all' || t.n > 0).map((t) => t.key);
      if (usable.length < 2) return;
      const at = usable.indexOf(tab);
      setTab(usable[(at + dir + usable.length) % usable.length]);
    },
    [tabs, tab],
  );

  const onDialogKey = (e: React.KeyboardEvent) => {
    if (e.key !== 'Tab') return;
    e.preventDefault();
    if (!s.idle) cycleTab(e.shiftKey ? -1 : 1);
  };

  const noHitsAllTypes = s.total === 0;

  return (
    <>
      <button
        className="search-trigger"
        onClick={openPalette}
        title="Global search — repos, secrets, clusters, namespaces (⌘K)"
      >
        <span className="search-trigger-label">
          <Icon name="search" />
          Search repos, clusters, secrets…
        </span>
        <kbd>⌘K</kbd>
      </button>

      {/* PORTALLED to <body>, not rendered in place. TopBar mounts this component,
          so an in-place overlay is a DOM descendant of `.topbar` — and
          `.topbar button { background: transparent }` (specificity 0,1,1) then beats
          every single-class rule in the palette, which silently flattened the
          selected type tab and its accent border. A fixed-position, full-screen
          modal has no business being a child of the header; portalling fixes that
          whole class of bleed rather than escalating specificity per rule. */}
      {open &&
        createPortal(
          <div className="search-overlay">
          <div
            className="search-panel"
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-label="Global search"
            onKeyDown={onDialogKey}
          >
            <Command shouldFilter={false} loop>
              <div className="search-input-row">
                {/* The chip stands in for the prefix the user typed, so the caret
                    sits after it and the input reads as scoped rather than as a
                    query that happens to contain a colon. */}
                {s.alias && (
                  <button
                    className="search-scope-chip"
                    title="Remove this scope"
                    onClick={() => setValue(s.term)}
                  >
                    {s.alias}:
                    <Icon name="close" size={12} />
                  </button>
                )}
                <Command.Input
                  ref={inputRef}
                  autoFocus
                  value={value}
                  onValueChange={setValue}
                  placeholder="Search repos, clusters, services, namespaces, secret keys…"
                />
                <kbd className="search-esc">esc</kbd>
              </div>

              {/* Tabs only once there is something to count. */}
              {!s.idle && (
                <div className="search-tabs" role="tablist">
                  {tabs.map((t) => (
                    <button
                      key={t.key}
                      role="tab"
                      aria-selected={tab === t.key}
                      className={`search-tab${tab === t.key ? ' search-tab--on' : ''}${
                        t.n === 0 && t.key !== 'all' ? ' search-tab--zero' : ''
                      }`}
                      onClick={() => setTab(t.key)}
                    >
                      {t.key !== 'all' && (
                        <span className="search-tab__dot" style={{ background: t.color }} />
                      )}
                      {t.label}
                      <span className="search-tab__n">{t.n}</span>
                    </button>
                  ))}
                </div>
              )}

              {/* Dimmed, not cleared, while the next result set loads — clearing
                  makes the list jump on every keystroke. */}
              <Command.List className={s.loading && s.hits.length > 0 ? 'is-stale' : undefined}>
                {s.error ? (
                  <div className="search-empty">
                    <div className="search-empty__title">Search is unavailable</div>
                    <div className="search-empty__body">{s.error.message}</div>
                    <div className="search-empty__actions">
                      <button className="mini-btn" onClick={s.refetch}>
                        Retry
                      </button>
                    </div>
                  </div>
                ) : s.idle ? (
                  <IdleState
                    recents={recents}
                    onPick={(q) => {
                      setValue(q);
                      setTab('all');
                    }}
                  />
                ) : s.groups.length === 0 ? (
                  <div className="search-empty">
                    <div className="search-empty__title">
                      {noHitsAllTypes
                        ? `No match for “${s.term}”`
                        : `No ${metaFor(tab).label.toLowerCase()} match “${s.term}”`}
                    </div>
                    <div className="search-empty__body">
                      {noHitsAllTypes
                        ? 'Repo paths, cluster names, namespaces, service names, env-var names and secret key names are indexed. Values are never indexed.'
                        : `${s.total} result${s.total === 1 ? '' : 's'} exist in other entity types. Clear the filter to see them.`}
                    </div>
                    <div className="search-empty__actions">
                      <button
                        className="mini-btn"
                        onClick={() => {
                          setTab('all');
                          if (noHitsAllTypes) setValue(s.term);
                        }}
                      >
                        {noHitsAllTypes ? 'Search all types' : 'Clear the type filter'}
                      </button>
                    </div>
                    {/* The prototype offered a "Rebuild the index" button here. It
                        is dropped: the mutation behind it is root-only, so for most
                        people it would be a control that always fails. The job
                        lives on the Scheduler, where it is gated properly. */}
                    <WithheldBanner n={s.withheld} levels={me?.allowedLevels} />
                  </div>
                ) : (
                  <>
                    <WithheldBanner n={s.withheld} levels={me?.allowedLevels} />
                    {s.groups.map((g) => (
                      <Command.Group
                        key={g.type}
                        heading={
                          <span className="search-group-heading">
                            <span className="search-group__dot" style={{ background: g.color }} />
                            {g.label}
                            <span className="search-group__n">{g.total}</span>
                            {g.overflow && (
                              <button
                                className="search-group__all"
                                onClick={() => setTab(g.type)}
                              >
                                Show all {g.total} →
                              </button>
                            )}
                          </span>
                        }
                      >
                        {g.items.map((h) => {
                          const why = whyFor(h, s.term);
                          return (
                            <Command.Item
                              key={`${h.entityType}:${h.entityId}`}
                              value={`${h.entityType}:${h.entityId}:${h.title}`}
                              onSelect={() => go(h.route)}
                            >
                              <span
                                className="hit-dot"
                                style={{ background: metaFor(h.entityType).color }}
                              />
                              <span className="hit-main">
                                <span className="hit-title">
                                  <Highlighted text={h.title} tokens={s.marks} />
                                </span>
                                {h.subtitle && (
                                  <span className="hit-sub">
                                    <Highlighted text={h.subtitle} tokens={s.marks} />
                                  </span>
                                )}
                                {/* WHY this row matched. Without it a body hit — an
                                    env-var key inside a bundle — rendered as a title
                                    with nothing highlighted, which looked like a fuzzy
                                    match rather than "the key you typed lives here". */}
                                {why && (
                                  <span className={`hit-why hit-why--${why.kind}`}>
                                    <span className="hit-why__label">{why.label}</span>
                                    {/* A fuzzy hit has no token to show — a bare
                                        label beats inventing a fragment. */}
                                    {why.value && (
                                      <span className="hit-why__value">
                                        <Highlighted text={why.value} tokens={s.marks} />
                                      </span>
                                    )}
                                  </span>
                                )}
                              </span>
                              {/* Tier pips, not a numeric score — the old `rank`
                                  was constant for the great majority of queries,
                                  so the bar was drawing precision that wasn't
                                  there. See TIER_LABEL / MATCH_TIER. */}
                              <span className="hit-rail">
                                <span
                                  className="hit-tier"
                                  title={TIER_LABEL[h.tier] ?? 'match'}
                                  aria-label={`match quality: ${TIER_LABEL[h.tier] ?? 'match'}`}
                                >
                                  {TIER_PIPS.map((n) => (
                                    <span
                                      key={n}
                                      className={`hit-tier__pip${h.tier >= n ? ' hit-tier__pip--on' : ''}`}
                                      style={
                                        h.tier >= n
                                          ? { background: metaFor(h.entityType).color }
                                          : undefined
                                      }
                                    />
                                  ))}
                                </span>
                              </span>
                            </Command.Item>
                          );
                        })}
                      </Command.Group>
                    ))}
                  </>
                )}
              </Command.List>
            </Command>

            <div className="search-foot">
              <span>
                <kbd>↑↓</kbd> navigate
              </span>
              <span>
                <kbd>⏎</kbd> open
              </span>
              <span>
                <kbd>⇥</kbd> next type
              </span>
              <span className="spacer" />
              {!s.idle && s.total > 0 && (
                <button className="mini-btn" onClick={seeAll}>
                  See all {s.shownTotal} results
                </button>
              )}
            </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}

/**
 * Idle state (term < 2 chars) — deliberately not blank.
 *
 * An empty palette teaches nothing; this one shows what you searched before, the
 * scopes that exist, and the one non-obvious fact about this index: secret KEY
 * names are searchable, so the thing you half-remember finds the bundle holding it.
 */
function IdleState({ recents, onPick }: { recents: Recent[]; onPick: (q: string) => void }) {
  return (
    <div className="search-idle">
      {recents.length > 0 && (
        <>
          <div className="search-idle__label">Recent</div>
          {recents.map((r) => (
            <button key={r.q} className="search-recent" onClick={() => onPick(r.q)}>
              <Icon name="clock" size={14} />
              <span className="search-recent__q">{r.q}</span>
              <span className="search-recent__meta">
                {r.hits != null ? `${r.hits} hit${r.hits === 1 ? '' : 's'} · ` : ''}
                {relAge(r.at)}
              </span>
            </button>
          ))}
        </>
      )}

      <div className="search-idle__label">Scope a search</div>
      <div className="search-hints">
        {SCOPE_HINTS.map((h) => (
          <button key={h.prefix} className="search-hint" onClick={() => onPick(h.example)}>
            <code className="search-hint__prefix">{h.prefix}</code>
            <span className="search-hint__what">{h.what}</span>
          </button>
        ))}
      </div>

      <div className="search-tip">
        <Icon name="question" size={16} />
        <span>
          Secret <strong>key names</strong> are indexed, so searching{' '}
          <code>MONGO_SCIO_URL</code> finds the bundle that contains it. Values are never indexed.
        </span>
      </div>
    </div>
  );
}
